import NextLink from 'next/link'
import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'

import { PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import CircularProgress from 'app/KinerjaShared/components/CircularProgress'
import { Header } from 'app/KinerjaShared/components/Header'
import { TahapPerencanaanCard } from 'app/KinerjaGpkPage/components/TahapPerencanaanCard'
import { useApiGetPerencanaanList } from 'api/kinerja/gpk/perencanaan'

const PERENCANAAN_STEPS = [
  {
    title: 'Periksa Perencanaan',
    subtitle: 'Oleh Tim Kinerja dan Pejabat Penilai Kinerja',
    imagePath: '/illustrations/img-clip-board-summary.png',
  },
  {
    title: 'Setujui Perencanaan',
    subtitle: 'Oleh Pejabat Penilai Kinerja',
    imagePath: '/illustrations/img-document-penci-fill.png',
  },
  {
    title: 'Tahap Pelaksanaan',
    subtitle: 'Oleh Guru Pendidikan Khusus',
    imagePath: '/illustrations/img-thumbs-up.png',
  },
]

export function Perencanaan() {
  // -----------------------------------------
  // Get perencanaan list data
  // -----------------------------------------
  const { data: perencanaanListData, isLoading } = useApiGetPerencanaanList({
    params: {
      isMetaOnly: true,
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
  })

  const {
    totalCount,
    totalApprovedCount,
    totalUnsubmittedCount,
    totalWaitingApprovalPPKCount,
  } = useMemo(() => {
    const meta = perencanaanListData?.meta

    if (!meta)
      return {
        totalCount: 0,
        totalApprovedCount: 0,
        totalUnsubmittedCount: 0,
      }

    return {
      totalCount: meta.totalCount?.ALL,
      totalApprovedCount: meta.totalCount?.APPROVED + meta.totalCount?.READY,
      totalUnsubmittedCount:
        meta.totalCount?.DRAFT + meta.totalCount?.NOT_FOUND,
      totalWaitingApprovalPPKCount: meta.totalCount?.WAITING_APPROVAL_PPK,
    }
  }, [perencanaanListData?.meta])

  return (
    <main>
      <div className="bg-white">
        <Header
          title="Perencanaan Kinerja"
          className="py-10 px-6 mx-auto max-w-7xl "
        />
      </div>

      <div className="w-full gap-4 p-6 space-y-6 max-w-7xl mx-auto">
        <section className="shadow-card rounded-lg">
          <div className="px-6 pt-6 pb-9 bg-blue-0 rounded-t-lg">
            <Text variant="heading-md" as="h3" className="mb-6">
              Periksa Perencanaan dan pantau progres pemeriksaan Anda di sini:
            </Text>

            <div className="flex gap-6">
              {/* Setujui sekaligus */}
              <div className="rounded-lg p-6 bg-white border border-subdued w-full">
                <div className="flex space-x-2 mb-1 items-start">
                  <Text variant="heading-sm" as="h4">
                    Setujui sekaligus
                  </Text>
                  <Badge variant="informational">
                    {totalWaitingApprovalPPKCount}
                  </Badge>
                </div>
                <Text color="subdued" className="mb-4">
                  Perencanaan yang sudah diperiksa Tim Kinerja dapat Anda
                  setujui sekaligus.
                </Text>
                <NextLink
                  href={`${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/setujui-sekaligus`}
                  passHref
                >
                  <Button
                    as="a"
                    color="black"
                    size="lg"
                    type={undefined}
                    className="!inline-flex"
                  >
                    Setujui sekaligus
                  </Button>
                </NextLink>
              </div>

              {/* Periksa satu per satu */}
              <div className="rounded-lg p-6 bg-white border border-subdued w-full">
                <Text variant="heading-sm" as="h4" className="mb-1">
                  Periksa satu per satu
                </Text>
                <Text color="subdued" className="mb-4">
                  Periksa tiap perencanaan untuk setujui atau arahkan revisi.
                </Text>
                <NextLink
                  href={`${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/periksa`}
                  passHref
                >
                  <Button
                    as="a"
                    color="black"
                    size="lg"
                    type={undefined}
                    className="!inline-flex"
                  >
                    Periksa satu per satu
                  </Button>
                </NextLink>
              </div>

              {/* Belum diajukan */}
              <div className="rounded-lg p-6 bg-white border border-subdued w-full">
                <div className="flex space-x-2 items-start mb-1">
                  <Text variant="heading-sm" as="h4">
                    Belum diajukan Guru Pendidikan Khusus
                  </Text>
                  <Badge variant="critical">{totalUnsubmittedCount}</Badge>
                </div>
                <Text color="subdued" className="mb-4">
                  Kabari Guru Pendidikan Khusus untuk segera mengajukan.
                </Text>
                <NextLink
                  href={`${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/belum-diajukan`}
                  passHref
                >
                  <Button
                    as="a"
                    color="white"
                    size="lg"
                    type={undefined}
                    className="!inline-flex"
                  >
                    Cek Daftar Guru Pendidikan Khusus
                  </Button>
                </NextLink>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8 p-6 rounded-b-lg bg-white">
            <div className="w-20 h-20">
              <CircularProgress
                value={totalApprovedCount}
                maxValue={totalCount}
              />
            </div>
            <div className="flex-1">
              <Text variant="heading-lg" as="h4">
                Perencanaan yang sudah disetujui Pejabat Penilai Kinerja
              </Text>
            </div>
            <NextLink
              href={`${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/disetujui`}
              passHref
            >
              <Button
                as={undefined}
                color="blue"
                size="lg"
                className="min-w-60 ml-auto"
                isLoading={isLoading}
                disabled={totalApprovedCount === 0 || isLoading}
              >
                {isLoading ? 'Memuat...' : 'Cek'}
              </Button>
            </NextLink>
          </div>
        </section>

        <section>
          <Text variant="heading-md" as="h3" className="mb-6">
            Tahap Perencanaan Kinerja
          </Text>
          <div className="grid grid-cols-3 gap-2">
            {PERENCANAAN_STEPS.map((step, index) => (
              <TahapPerencanaanCard
                key={index}
                orderNumber={index + 1}
                title={step.title}
                subtitle={step.subtitle}
                imagePath={step.imagePath}
              />
            ))}
          </div>
        </section>
      </div>
    </main>
  )
}
